import { doc, onSnapshot, setDoc } from 'firebase/firestore'
import { db } from '../../firebase'
import { normalizeMailboxList } from '../pages/mail/utils/feedWindow'

export type MailSource = 'imap' | 'graph'
export type MailSourceMode = 'imap' | 'graph' | 'both'

export interface MailSourceConfigDoc {
  mode: MailSourceMode
  /** Mirrored by the server sweep: lower-case addresses of ALL configured mailboxes (paused included). */
  mailboxes?: string[]
  updatedAt?: any
}

/** Extra config carried alongside the mode; absent on a read error (caller keeps what it had). */
export interface MailSourceConfigExtras {
  /** null = the doc has no usable list (missing doc / field): callers fall back to their default. */
  mailboxes: string[] | null
}

export const MAIL_SOURCE_CONFIG_DOC_PATH = {
  collection: 'system_config',
  id: 'mail_source',
} as const

export const DEFAULT_MAIL_SOURCE_MODE: MailSourceMode = 'both'

export function normalizeMailSource(value: unknown): MailSource {
  return value === 'graph' ? 'graph' : 'imap'
}

export function normalizeMailSourceMode(value: unknown): MailSourceMode {
  return value === 'imap' || value === 'graph' || value === 'both' ? value : DEFAULT_MAIL_SOURCE_MODE
}

export function listenToMailSourceMode(
  onChange: (mode: MailSourceMode, config?: MailSourceConfigExtras) => void
): () => void {
  const ref = doc(db, MAIL_SOURCE_CONFIG_DOC_PATH.collection, MAIL_SOURCE_CONFIG_DOC_PATH.id)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onChange(DEFAULT_MAIL_SOURCE_MODE, { mailboxes: null })
        return
      }
      const data = snap.data() as any
      const mailboxes = normalizeMailboxList(data?.mailboxes)
      onChange(normalizeMailSourceMode(data?.mode), { mailboxes: mailboxes.length > 0 ? mailboxes : null })
    },
    () => {
      // On read errors, fail open so we don't break mail UI. No `config` argument: the listener is
      // dead, so callers must keep whatever mailbox list they already had rather than reset it.
      onChange(DEFAULT_MAIL_SOURCE_MODE)
    }
  )
}

export async function setMailSourceMode(mode: MailSourceMode): Promise<void> {
  const ref = doc(db, MAIL_SOURCE_CONFIG_DOC_PATH.collection, MAIL_SOURCE_CONFIG_DOC_PATH.id)
  const payload: MailSourceConfigDoc = {
    mode,
    updatedAt: new Date(),
  }
  await setDoc(ref, payload as any, { merge: true })
}


